/**
 * Site header: sticky bar, search dropdown, mobile overlay accordion.
 */
const ARCADIA_MOBILE_NAV_MAX = 1024;
const ARCADIA_HEADER_STICK_AFTER = 64;
const ARCADIA_HEADER_UNSTICK_BELOW = 16;

function arcadiaIsMobileNavViewport() {
	return window.matchMedia(`(max-width: ${ARCADIA_MOBILE_NAV_MAX}px)`).matches;
}

function arcadiaGetHeaderBar() {
	const headerRoot = document.getElementById('arcadia-header');
	const bar =
		headerRoot?.closest('header') ||
		headerRoot?.closest('.wp-block-template-part') ||
		document.querySelector('header.wp-block-template-part');

	if (bar) {
		bar.classList.add('arcadia-header-container');
	}

	return bar || null;
}

/**
 * Toggle .is-stuck with hysteresis (75px → 60px). A single threshold
 * would flicker because the shrink moves the toggle point.
 */
function arcadiaSyncStickyHeader() {
	const bar = arcadiaGetHeaderBar();
	if (!bar) {
		return;
	}

	const y = window.scrollY || document.documentElement.scrollTop || 0;
	const isStuck = bar.classList.contains('is-stuck');

	if (!isStuck && y >= ARCADIA_HEADER_STICK_AFTER) {
		bar.classList.add('is-stuck');
	} else if (isStuck && y <= ARCADIA_HEADER_UNSTICK_BELOW) {
		bar.classList.remove('is-stuck');
	}
}

function arcadiaInitHeaderSearchA11y() {
	const dropdown = document.querySelector('.site-header__search-dropdown');
	const trigger = document.querySelector('#arcadia-header-search-trigger');
	const panel = document.querySelector('#arcadia-header-search-panel');
	const input = document.querySelector('#arcadia-search-input');
	if (!dropdown || !trigger) {
		return;
	}

	const setOpen = (isOpen, { focusInput = false } = {}) => {
		dropdown.classList.toggle('is-open', isOpen);
		trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
		trigger.setAttribute('aria-label', isOpen ? 'Close search' : 'Search');

		if (isOpen) {
			document
				.querySelectorAll('#arcadia-header .level-0 > .menu-item.has-children.is-desktop-open')
				.forEach((item) => item.classList.remove('is-desktop-open'));
		}

		if (isOpen && focusInput && input) {
			window.requestAnimationFrame(() => input.focus());
		}
	};

	trigger.addEventListener('click', () => {
		const willOpen = !dropdown.classList.contains('is-open');
		setOpen(willOpen, { focusInput: willOpen });
	});

	document.addEventListener('click', (event) => {
		if (dropdown.classList.contains('is-open') && !dropdown.contains(event.target)) {
			setOpen(false);
		}
	});

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && dropdown.classList.contains('is-open')) {
			setOpen(false);
			trigger.focus();
		}
	});

	if (panel) {
		panel.addEventListener('keydown', (event) => {
			if (event.key === 'Escape') {
				setOpen(false);
				trigger.focus();
			}
		});
	}

	setOpen(false);
}

function arcadiaCollapseMobileMegaPanels() {
	const root = document.querySelector('#arcadia-header-menu .site-mega-nav ul.level-0');
	if (!root) {
		return;
	}
	root.querySelectorAll(':scope > .menu-item.has-children.is-expanded').forEach((li) => {
		li.classList.remove('is-expanded');
		const link = li.querySelector(':scope > .menu-link[aria-expanded]');
		if (link) {
			link.setAttribute('aria-expanded', 'false');
		}
	});
}

function arcadiaInitMobileMegaAccordion() {
	const root = document.querySelector('#arcadia-header-menu .site-mega-nav ul.level-0');
	if (!root) {
		return;
	}

	root.addEventListener('click', (e) => {
		if (!arcadiaIsMobileNavViewport() || !document.body.classList.contains('nav-active')) {
			return;
		}

		const li = e.target.closest('.menu-item.has-children');
		if (!li || li.parentElement !== root) {
			return;
		}

		const link = li.querySelector(':scope > .menu-link');
		if (!link || (!link.contains(e.target) && e.target !== link)) {
			return;
		}

		e.preventDefault();

		const willExpand = !li.classList.contains('is-expanded');

		root.querySelectorAll(':scope > .menu-item.has-children.is-expanded').forEach((openLi) => {
			if (openLi === li) {
				return;
			}
			openLi.classList.remove('is-expanded');
			const openLink = openLi.querySelector(':scope > .menu-link[aria-expanded]');
			if (openLink) {
				openLink.setAttribute('aria-expanded', 'false');
			}
			const openWrap = openLi.querySelector(':scope > .mega-panel-wrap');
			if (openWrap) {
				openWrap.scrollTop = 0;
			}
		});

		li.classList.toggle('is-expanded', willExpand);
		link.setAttribute('aria-expanded', willExpand ? 'true' : 'false');

		const wrap = li.querySelector(':scope > .mega-panel-wrap');
		if (wrap) {
			wrap.scrollTop = 0;
		}
	});
}

function arcadiaInitMobileNavToggle() {
	const toggle = document.getElementById('arcadia-mobile-nav-toggle');
	const menu = document.getElementById('arcadia-header-menu');
	if (!toggle) {
		return;
	}

	const focusableSelector = [
		'a[href]',
		'button:not([disabled])',
		'input:not([disabled])',
		'select:not([disabled])',
		'textarea:not([disabled])',
		'[tabindex]:not([tabindex="-1"])'
	].join(', ');
	let previouslyFocusedElement = null;

	const getMenuFocusableElements = () => {
		if (!menu) {
			return [];
		}
		return Array.from(menu.querySelectorAll(focusableSelector)).filter((el) => {
			if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') {
				return false;
			}
			const style = window.getComputedStyle(el);
			return style.display !== 'none' && style.visibility !== 'hidden';
		});
	};

	const setNavActive = (active) => {
		const wasActive = document.body.classList.contains('nav-active');
		if (active && !wasActive) {
			previouslyFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
		}

		document.body.classList.toggle('nav-active', active);
		toggle.setAttribute('aria-expanded', active ? 'true' : 'false');
		toggle.setAttribute('aria-label', active ? 'Close menu' : 'Menu');
		if (!active) {
			arcadiaCollapseMobileMegaPanels();
		}

		window.requestAnimationFrame(() => {
			if (active) {
				const focusables = getMenuFocusableElements();
				if (focusables.length > 0) {
					focusables[0].focus();
				}
				return;
			}

			const focusTarget =
				previouslyFocusedElement && document.contains(previouslyFocusedElement)
					? previouslyFocusedElement
					: toggle;
			focusTarget.focus();
		});
	};

	toggle.addEventListener('click', () => {
		if (arcadiaIsMobileNavViewport()) {
			setNavActive(!document.body.classList.contains('nav-active'));
		}
	});

	document.addEventListener('keydown', (e) => {
		if (!document.body.classList.contains('nav-active')) {
			return;
		}

		if (e.key === 'Escape') {
			setNavActive(false);
			return;
		}

		if (e.key !== 'Tab' || !menu) {
			return;
		}

		const focusables = getMenuFocusableElements();
		if (focusables.length === 0) {
			e.preventDefault();
			return;
		}

		const first = focusables[0];
		const last = focusables[focusables.length - 1];
		const current = document.activeElement;

		if (e.shiftKey) {
			if (current === first || !menu.contains(current)) {
				e.preventDefault();
				last.focus();
			}
		} else if (current === last || !menu.contains(current)) {
			e.preventDefault();
			first.focus();
		}
	});

	window.addEventListener('resize', () => {
		if (!arcadiaIsMobileNavViewport() && document.body.classList.contains('nav-active')) {
			setNavActive(false);
		}
	});
}

export function initArcadiaSiteHeader() {
	if (!document.getElementById('arcadia-header')) {
		return;
	}

	arcadiaInitHeaderSearchA11y();
	arcadiaInitMobileMegaAccordion();
	arcadiaInitMobileNavToggle();
	arcadiaSyncStickyHeader();

	window.addEventListener('load', arcadiaSyncStickyHeader);
	window.addEventListener('pageshow', arcadiaSyncStickyHeader);
	window.addEventListener('resize', arcadiaSyncStickyHeader);
	window.addEventListener('scroll', arcadiaSyncStickyHeader, { passive: true });
}
